import {inject, Injector, ProviderToken} from '@angular/core';
import {catchError, distinctUntilChanged, EMPTY, filter, first, map, Observable, pipe, switchMap, tap} from 'rxjs';
import {patchState, signalStoreFeature, withMethods, withState} from '@ngrx/signals';
import {
	addEntity,
	EntityId,
	removeEntities,
	removeEntity,
	setAllEntities,
	setEntity,
	updateEntity,
	withEntities,
} from '@ngrx/signals/entities';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {CurrentTenantStore} from 'src/app/services/tenant/current-tenant.store';
import {stateChanges} from 'src/app/services/shared/state-changes';
import {LoadError, toLoadError} from 'src/app/services/shared/load-error.model';

/**
 * Reusable @ngrx/signals feature for a **tenant-keyed server-cache collection**
 * (ADR-0001 — the "server state" bucket): `withEntities` for the collection, loaded for the active
 * tenant. A page load serves a single workspace (switching reloads the app, see
 * CurrentTenantStore), so the cache never has to drop another tenant's data.
 *
 * Usage — define a `providedIn: 'root'` signalStore that supplies the back-service,
 * the id selector, and the fetch:
 *
 *   export const CatalogTagStore = signalStore(
 *     { providedIn: 'root' },
 *     withTenantScope<CatalogTag, CatalogTagService>({
 *       service: CatalogTagService,
 *       selectId: t => t.tagId,
 *       fetch: (svc, tenantId) => svc.getAllCatalogTagByTenantId(tenantId),
 *     }),
 *   );
 *
 * Readers: call `store.load()` on init and read `store.entities()` / `store.loading()` / `store.error()` (shown with
 * `app-error-state`, `refresh()` to retry) — or, in a flow that combines the collection with other loads,
 * `ensureLoaded()` / `entitiesChanges()`.
 * Writers: `store.addItem()` / `store.updateItem()` / `store.removeItem()` / `store.removeItems()`
 * after the backend mutation, so every reader updates live.
 */
interface TenantScopeState {
	loading: boolean;
	loadedTenantId: string | null;
	/** Why the collection could not be loaded; null once it is. Not loaded, so the next load() tries again. */
	error: LoadError | null;
}

export function withTenantScope<T, S>(config: {
	service: ProviderToken<S>;
	selectId: (entity: T) => EntityId;
	fetch: (service: S, tenantId: string) => Observable<T[]>;
}) {
	return signalStoreFeature(
		withEntities<T>(),
		withState<TenantScopeState>({loading: false, loadedTenantId: null, error: null}),
		withMethods((store) => {
			const tenant = inject(CurrentTenantStore);
			const service = inject(config.service);
			const state$ = stateChanges(store, inject(Injector));

			const fetchForTenant = rxMethod<string>(
				pipe(
					tap(() => patchState(store, {loading: true, error: null})),
					switchMap((tenantId) =>
						config.fetch(service, tenantId).pipe(
							tap((items) => {
								patchState(
									store,
									setAllEntities(items, {selectId: config.selectId}),
									{loading: false, loadedTenantId: tenantId},
								);
							}),
							catchError(error => {
								patchState(store, {loading: false, error: toLoadError(error)});
								return EMPTY;
							}),
						),
					),
				),
			);

			/** Loads the collection for the active tenant (cache-aware: skips if already loaded). */
			const load = (): void => {
				const tenantId = tenant.currentTenantId();
				if (tenantId && store.loadedTenantId() !== tenantId && !store.loading()) {
					fetchForTenant(tenantId);
				}
			};

			return {
				load,
				/**
				 * The collection once loaded — at once when it already is — for a flow that combines it with other
				 * loads (forkJoin). A failed fetch answers what the store holds rather than hang.
				 */
				ensureLoaded(): Observable<T[]> {
					load();
					return state$.pipe(filter(state => !state.loading), first(), map(() => store.entities()));
				},
				/** The collection on subscribe, then after each change — for subscribers that keep their own copy in sync. */
				entitiesChanges(): Observable<T[]> {
					return state$.pipe(map(() => store.entities()), distinctUntilChanged());
				},
				/** Forces a refetch for the active tenant. */
				refresh(): void {
					const tenantId = tenant.currentTenantId();
					if (tenantId) {
						fetchForTenant(tenantId);
					}
				},
				/** Marks the cache stale so the next load() refetches — without an immediate fetch (e.g. on logout / permission change). */
				invalidate(): void {
					patchState(store, {loadedTenantId: null});
				},
				/** Adds an item after a backend create. */
				addItem(entity: T): void {
					patchState(store, addEntity(entity, {selectId: config.selectId}));
				},
				/** Adds or replaces an item after a backend create/update (upsert). */
				setItem(entity: T): void {
					patchState(store, setEntity(entity, {selectId: config.selectId}));
				},
				/** Patches an item after a backend update (a rename…). */
				updateItem(id: EntityId, changes: Partial<T>): void {
					patchState(store, updateEntity({id, changes}, {selectId: config.selectId}));
				},
				/** Removes an item after a backend delete. */
				removeItem(id: EntityId): void {
					patchState(store, removeEntity(id));
				},
				/** Removes several items after a backend bulk delete. */
				removeItems(ids: EntityId[]): void {
					patchState(store, removeEntities(ids));
				},
			};
		}),
	);
}
